import { InventoryAuditTimelineAction } from "../enums";
import { endpointsMetadata } from "../infrastructure/routes/endpointsMetadata";
import { IHistoryConfig, IUserAuditLogItem } from "../components/history/models/historyModels";

export const INVENTORY_HISTORY_CUTOFF_DATE = new Date("2026-04-11");

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function formatNumber(value: unknown): string {
  if (value === null || value === undefined || value === "") return "–";
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  // Preserve up to 3 decimals but trim trailing zeros.
  return n
    .toFixed(3)
    .replace(/\.?0+$/, "");
}

function formatMoney(value: unknown): string {
  if (value === null || value === undefined || value === "") return "–";
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return `${n.toFixed(2)} EUR`;
}

export function generateInventoryAuditText(
  action: number,
  payload: Record<string, any> | null | undefined,
  actorDisplayName: string | null | undefined,
): string {
  const actor = escapeHtml(actorDisplayName ?? "Системата");
  const p = payload ?? {};

  switch (action as InventoryAuditTimelineAction) {
    case InventoryAuditTimelineAction.ItemCreated: {
      const name = p.name ? `"${escapeHtml(String(p.name))}"` : "продукт";
      return `${actor} създаде ${name} (начално количество ${formatNumber(p.initialQuantity)})`;
    }
    case InventoryAuditTimelineAction.ItemEdited: {
      const changes: string[] = [];
      if (p._mergedChanges) {
        for (const change of p._mergedChanges as Array<{ action: number; payload: Record<string, any> }>) {
          const cp = change.payload ?? {};
          switch (change.action as InventoryAuditTimelineAction) {
            case InventoryAuditTimelineAction.PriceChanged:
              changes.push(`промени цената от ${formatMoney(cp.oldValue)} на ${formatMoney(cp.newValue)}`);
              break;
            case InventoryAuditTimelineAction.CostPriceChanged:
              changes.push(`промени себестойността от ${formatMoney(cp.oldValue)} на ${formatMoney(cp.newValue)}`);
              break;
            case InventoryAuditTimelineAction.LowStockThresholdChanged:
              changes.push(`промени прага за ниска наличност от ${formatNumber(cp.oldValue)} на ${formatNumber(cp.newValue)}`);
              break;
            case InventoryAuditTimelineAction.CategoryChanged:
              changes.push("премести продукта в друга категория");
              break;
            case InventoryAuditTimelineAction.ExpirationDateSet:
              changes.push("зададе срок на годност");
              break;
          }
        }
      }
      if (changes.length > 0) {
        return `${actor} редактира продукта: ${changes.join(", ")}`;
      }
      return `${actor} редактира продукта`;
    }

    case InventoryAuditTimelineAction.ItemDeleted:
      return `${actor} изтри продукта`;

    case InventoryAuditTimelineAction.ItemRestored:
      return `${actor} възстанови продукта`;

    case InventoryAuditTimelineAction.PriceChanged:
      return `${actor} промени цената от ${formatMoney(p.oldValue)} на ${formatMoney(p.newValue)}`;

    case InventoryAuditTimelineAction.CostPriceChanged:
      return `${actor} промени себестойността от ${formatMoney(p.oldValue)} на ${formatMoney(p.newValue)}`;

    case InventoryAuditTimelineAction.LowStockThresholdChanged:
      return `${actor} промени прага за ниска наличност от ${formatNumber(p.oldValue)} на ${formatNumber(p.newValue)}`;

    case InventoryAuditTimelineAction.CategoryChanged:
      return `${actor} премести продукта в друга категория`;

    case InventoryAuditTimelineAction.ManualQuantityAdjustment: {
      const delta = Number(p.delta ?? 0);
      const sign = delta >= 0 ? "+" : "";
      const reason = p.reason ? ` (${escapeHtml(String(p.reason))})` : "";
      return `${actor} коригира наличността: ${formatNumber(p.oldQuantity)} → ${formatNumber(p.newQuantity)} (${sign}${formatNumber(delta)})${reason}`;
    }

    case InventoryAuditTimelineAction.TransferApplied: {
      const num = p.transferNumber ? `#D-${String(p.transferNumber).padStart(4, "0")}` : "доставка";
      const delta = formatNumber(p.delta);
      const qtyChange = (p.oldQuantity !== undefined && p.newQuantity !== undefined)
        ? `: ${formatNumber(p.oldQuantity)} → ${formatNumber(p.newQuantity)} (+${delta})`
        : `: +${delta}`;
      return `Доставка ${num} увеличи наличността${qtyChange}`;
    }

    case InventoryAuditTimelineAction.TransferReversed: {
      const num = p.transferNumber ? `#D-${String(p.transferNumber).padStart(4, "0")}` : "доставка";
      const delta = formatNumber(Math.abs(Number(p.delta ?? 0)));
      const qtyChange = (p.oldQuantity !== undefined && p.newQuantity !== undefined)
        ? `: ${formatNumber(p.oldQuantity)} → ${formatNumber(p.newQuantity)} (−${delta})`
        : `: −${delta}`;
      return `Доставка ${num} намали наличността${qtyChange}`;
    }

    case InventoryAuditTimelineAction.ItemSold: {
      const soldQty = formatNumber(p.soldQuantity ?? p.quantity);
      const orderDisplay = p.orderNumber
        ? ` (поръчка #${String(p.orderNumber).padStart(4, "0")})`
        : "";
      const qtyChange = (p.oldQuantity !== undefined && p.newQuantity !== undefined)
        ? `: ${formatNumber(p.oldQuantity)} → ${formatNumber(p.newQuantity)}`
        : "";
      return `${actor} продаде ${soldQty} бр./гр. от продукта${orderDisplay}${qtyChange}`;
    }

    case InventoryAuditTimelineAction.NoteAdded:
      return `${actor} добави бележка`;

    case InventoryAuditTimelineAction.NoteEdited:
      return `${actor} редактира бележка`;

    case InventoryAuditTimelineAction.NoteDeleted:
      return `${actor} изтри бележка`;

    case InventoryAuditTimelineAction.ExpirationDateSet:
      return `${actor} зададе срок на годност`;

    default:
      return `${actor} извърши действие (${action})`;
  }
}

export type InventoryAuditCategory = "lifecycle" | "quantity" | "notes";

export function getInventoryAuditActionCategory(action: number): InventoryAuditCategory {
  const a = action as InventoryAuditTimelineAction;

  if (
    [
      InventoryAuditTimelineAction.ItemCreated,
      InventoryAuditTimelineAction.ItemEdited,
      InventoryAuditTimelineAction.ItemDeleted,
      InventoryAuditTimelineAction.ItemRestored,
      InventoryAuditTimelineAction.PriceChanged,
      InventoryAuditTimelineAction.CostPriceChanged,
      InventoryAuditTimelineAction.LowStockThresholdChanged,
      InventoryAuditTimelineAction.CategoryChanged,
    ].includes(a)
  )
    return "lifecycle";

  if (
    [
      InventoryAuditTimelineAction.ManualQuantityAdjustment,
      InventoryAuditTimelineAction.TransferApplied,
      InventoryAuditTimelineAction.TransferReversed,
      InventoryAuditTimelineAction.ItemSold,
      InventoryAuditTimelineAction.ExpirationDateSet,
    ].includes(a)
  )
    return "quantity";

  return "notes";
}

export const INVENTORY_AUDIT_CATEGORY_COLORS: Record<InventoryAuditCategory, string> = {
  lifecycle: "#6366f1",
  quantity: "#10b981",
  notes: "#3b82f6",
};

const dot = (color: string) =>
  `<span style="display:inline-block;width:9px;height:9px;border-radius:50%;background:${color};margin-right:8px;vertical-align:middle;flex-shrink:0"></span>`;

export const INVENTORY_AUDIT_COLOR_LEGEND_HTML = `
<div style="line-height:2;min-width:180px">
  <div>${dot("#6366f1")}Продукт</div>
  <div>${dot("#10b981")}Наличност</div>
  <div>${dot("#3b82f6")}Бележки</div>
</div>`.trim();

/** Actions that represent field-specific changes logged alongside ItemEdited. */
const FIELD_CHANGE_ACTIONS = new Set<InventoryAuditTimelineAction>([
  InventoryAuditTimelineAction.PriceChanged,
  InventoryAuditTimelineAction.CostPriceChanged,
  InventoryAuditTimelineAction.LowStockThresholdChanged,
  InventoryAuditTimelineAction.CategoryChanged,
  InventoryAuditTimelineAction.ExpirationDateSet,
]);

/** Max time gap (ms) between an ItemEdited entry and its field-change companions. */
const MERGE_WINDOW_MS = 5_000;

function parsePayload(raw: string | null): Record<string, any> {
  if (!raw) return {};
  try { return JSON.parse(raw); } catch { return {}; }
}

/**
 * Merges consecutive ItemEdited + field-change entries into a single combined entry.
 * Items are assumed to be sorted descending by timestamp (newest first).
 */
export function mergeInventoryAuditItems(items: IUserAuditLogItem[]): IUserAuditLogItem[] {
  const result: IUserAuditLogItem[] = [];
  let i = 0;

  while (i < items.length) {
    const current = items[i];
    const currentAction = current.action as InventoryAuditTimelineAction;

    // Check if this is an ItemEdited entry — look ahead for field changes
    if (currentAction === InventoryAuditTimelineAction.ItemEdited) {
      const editTime = new Date(current.timestamp).getTime();
      const mergedChanges: Array<{ action: number; payload: Record<string, any> }> = [];
      let j = i + 1;

      // Absorb following field-change entries within the time window
      while (j < items.length) {
        const next = items[j];
        const nextAction = next.action as InventoryAuditTimelineAction;
        const nextTime = new Date(next.timestamp).getTime();

        if (FIELD_CHANGE_ACTIONS.has(nextAction) && Math.abs(editTime - nextTime) <= MERGE_WINDOW_MS) {
          mergedChanges.push({ action: next.action, payload: parsePayload(next.payload) });
          j++;
        } else {
          break;
        }
      }

      // Also look backwards (field changes might come BEFORE ItemEdited in the list)
      let k = result.length - 1;
      while (k >= 0) {
        const prev = result[k];
        const prevAction = prev.action as InventoryAuditTimelineAction;
        const prevTime = new Date(prev.timestamp).getTime();

        if (FIELD_CHANGE_ACTIONS.has(prevAction) && Math.abs(editTime - prevTime) <= MERGE_WINDOW_MS) {
          mergedChanges.unshift({ action: prev.action, payload: parsePayload(prev.payload) });
          result.splice(k, 1);
          k--;
        } else {
          break;
        }
      }

      if (mergedChanges.length > 0) {
        const editPayload = parsePayload(current.payload);
        editPayload._mergedChanges = mergedChanges;
        result.push({
          ...current,
          payload: JSON.stringify(editPayload),
        });
      } else {
        result.push(current);
      }

      i = j;
      continue;
    }

    // Check if this is a field-change that might precede an ItemEdited
    if (FIELD_CHANGE_ACTIONS.has(currentAction)) {
      const currentTime = new Date(current.timestamp).getTime();
      // Look ahead to see if ItemEdited follows
      let foundEdit = false;
      let j = i + 1;
      while (j < items.length) {
        const next = items[j];
        const nextTime = new Date(next.timestamp).getTime();
        if (Math.abs(currentTime - nextTime) > MERGE_WINDOW_MS) break;
        if ((next.action as InventoryAuditTimelineAction) === InventoryAuditTimelineAction.ItemEdited) {
          foundEdit = true;
          break;
        }
        if (FIELD_CHANGE_ACTIONS.has(next.action as InventoryAuditTimelineAction)) {
          j++;
          continue;
        }
        break;
      }
      if (foundEdit) {
        // Don't emit yet — the ItemEdited handler will absorb this
        result.push(current);
        i++;
        continue;
      }
    }

    result.push(current);
    i++;
  }

  return result;
}

export const INVENTORY_HISTORY_CONFIG: IHistoryConfig = {
  historyEndpoint: endpointsMetadata.inventoryAuditLog.getItemHistory,
  historyQueryParam: "itemId",
  detailEndpoint: endpointsMetadata.inventoryAuditLog.getDetail,
  cutoffDate: INVENTORY_HISTORY_CUTOFF_DATE,
  cutoffDateLabel: "11.04.2026 г.",
  tooltipHtml: INVENTORY_AUDIT_COLOR_LEGEND_HTML,
  generateText: generateInventoryAuditText,
  getCategory: getInventoryAuditActionCategory,
  categoryColors: INVENTORY_AUDIT_CATEGORY_COLORS,
  mergeItems: mergeInventoryAuditItems,
};
